import { abortable } from '../../async/abort';
import type { AccessTokens, AuthState, Hash } from '.';
import { Auth } from './baseAuth';

export class WorkerAuth extends Auth {
  #accessToken: string | undefined = undefined;

  #refreshToken: string | undefined = undefined;

  #sessionToken: string | undefined = undefined;

  #shareToken: string | undefined = undefined;

  getAccessToken(): string | undefined {
    return this.#accessToken;
  }

  getRefreshToken(): string | undefined {
    return this.#refreshToken;
  }

  getSessionToken(): string | undefined {
    return this.#sessionToken;
  }

  getShareToken(): string | undefined {
    return this.#shareToken;
  }

  setShareToken(shareToken: string) {
    this.#shareToken = shareToken;
  }

  setAccessToken(accessToken: string): void {
    this.#accessToken = accessToken;
  }

  setRefreshToken(refreshToken?: string): void {
    this.#refreshToken = refreshToken;
  }

  setSessionToken(sessionToken: string): void {
    this.#sessionToken = sessionToken;
  }

  removeRefreshToken() {
    this.#refreshToken = undefined;
  }

  removeAccessToken() {
    this.#accessToken = undefined;
  }

  removeSessionToken() {
    this.#sessionToken = undefined;
  }

  removeShareToken() {
    this.#shareToken = undefined;
  }

  /**
   * Refresh the auth state from the parent window.
   *
   * @param state - the current state or hash
   * @returns the new auth state
   */
  override async handleRefresh(
    state: Hash<AccessTokens>,
    options: { signal?: AbortSignal; doorslam: boolean }
  ): Promise<AuthState> {
    const { signal, doorslam } = options;
    const current = await this.state();
    signal?.throwIfAborted();
    if (current.hash !== state) {
      return current;
    }

    let listener: ((state: AuthState) => void) | null = null;
    return abortable(
      new Promise<AuthState>((resolve) => {
        listener = onStateChange;
        this.addSubscriber(onStateChange);
        // request a refresh from the parent window
        self.postMessage({
          eventType: 'authRefresh',
          payload: {
            hash: state,
            doorslam,
          },
        });

        function onStateChange(newState: AuthState) {
          if (newState.hash !== state) {
            resolve(newState);
          }
        }
      }),
      signal
    ).finally(() => {
      if (listener) {
        this.removeSubscriber(listener);
      }
    });
  }
}

export default WorkerAuth;
